import { sdk } from '@needful-things-mvp/sdk';
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

import { AUTH_STORAGE_KEY, IS_DEVELOPMENT, handleUnauthorized } from './api';
import { resetClientState } from './reset';

interface AuthContextValue {
  isAuthenticated: boolean;
  /** True after an explicit sign-out, so the guard won't remember the URL. */
  signedOut: boolean;
  setAuthenticated: () => void;
  clearSession: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * The stored flag is ignored in development so the app always starts signed
 * out (the dev login is session-only). Production still restores the flag.
 */
function readStoredAuth(): boolean {
  if (IS_DEVELOPMENT) return false;
  return localStorage.getItem(AUTH_STORAGE_KEY) === '1';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setAuthenticatedState] = useState<boolean>(readStoredAuth);
  const [signedOut, setSignedOut] = useState(false);

  // Any SDK call that 401s (revoked/expired session) drops us back to login.
  useEffect(() => {
    sdk.setOnSessionExpired(() => handleUnauthorized());
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      signedOut,
      setAuthenticated() {
        // The real session lives in an httpOnly cookie; this is just a flag.
        // In development it stays in memory so a reload starts signed out.
        if (!IS_DEVELOPMENT) localStorage.setItem(AUTH_STORAGE_KEY, '1');
        setSignedOut(false);
        setAuthenticatedState(true);
      },
      clearSession() {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        sdk.clearSession();
        resetClientState();
        setSignedOut(true);
        setAuthenticatedState(false);
      },
    }),
    [isAuthenticated, signedOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
